/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * created by jpatel
 * Function elements: Count, Average, Dividers
 */
import { PlotElement } from "./plotElement";

export const wrapDynCountElement = function(Dyn) {
	let mediator = Dyn.mediator;

/**
 * Dyn.CountPercentDividersElementBase()
 * Base class for Count, Percentage, Dividers Elements
 */
class CountPercentDividersElementBase extends PlotElement {

    constructor() {
        super(Dyn);
    }

    get kCellMarginRoomy() { return 10 } // for initial fit assuming lots of space for text
    get kCellMarginTight() { return 2 }  // for tight fit, make margin smaller (and clip text to fit)


    /**
     * Get the font size to use for a section of a pie chart computed by Dyn.PieChartElement
     * @param pieSection {arcLength:{number}} - size of section in Radians
     * @returns {number} - font size (points?)
     */
    getPieSectionFontSize( pieSection ) {
        var fontSize = CountPercentDividersElementBase.minPieSectionFontSize,
            arc = pieSection.arcLength, // in radians
            radius = pieSection.r;

        if( arc >= 1.0 && radius >= 100 ) {
            fontSize = 20;
        } else if ( arc >= 0.5 && radius >= 50 ) {
            fontSize = 15;
        } else if ( arc >= 0.25 && radius >= 25 ) {
            fontSize = 12.5;
        } else if ( arc >= 0.13 && radius >= 13 ) {
            fontSize = 10;
        } else if ( arc >= 0.7 && radius >= 7 ) {
            fontSize = 6;
        }
        return Dyn.preferences.getFontSize( fontSize );
    }

    static minPieSectionFontSize = 4;

    /**
     * Get the font size to use for a cell formed by the x and y axis divisions
     * @param cell {Dyn.mathUtil.Rect} - cell size rectangle (has other params too)
     * @returns {Number} - font size (points?)
     */
    getCellFontSize( cell, contextHint ) {
        Dyn.assertValidRect( cell );
        var fontSize = 8,
            cellSize = Math.min( cell.width, cell.height );
        if( cellSize >= 100 ) {
            fontSize = 16;
        } else if ( cellSize >= 50 ) {
            fontSize = 12;
        } else if ( cellSize >= 25 ) {
            fontSize = 10;
        }
        // let old = Dyn.preferences.getFontSize( fontSize );
        // let new1 = Math.round( Math.min( Dyn.preferences.getFontSize( 16 ), cell.height * 0.8 )),
        // let new2 = Dyn.preferences.getFontSize( 16 );
        // console.log( "getCellFontSize", contextHint,  cell.width, cell.height, "old:"+old, "new1:"+new1, "new2:"+new2 )
        return Dyn.preferences.getFontSize( fontSize );
    }

    getBarFontSize( cell, contextHint ) {
        Dyn.assertValidRect( cell );
        // console.log( "getBarFontSize", contextHint,  cell.width, cell.height, Dyn.preferences.getFontSize( 16 ) )
        return Dyn.preferences.getFontSize( 16 );
    }

    /**
     * Get the font size to use for a histogram bar.
     * @param barRect {Dyn.mathUtil.Rect} - bar size rectangle (has other params too)
     * @param isHorizonal
     * @returns {number} - font size
     */
    getHistogramFontSize( barRect, isHorizonal ) {
        var fontSize = 8,
            barWidth = isHorizonal ? barRect.width : barRect.height;
        if( barWidth >= 100 ) {
            fontSize = 16;
        } else if ( barWidth >= 50 ) {
            fontSize = 12;
        } else if ( barWidth >= 25 ) {
            fontSize = 10;
        }
        return Dyn.preferences.getFontSize( fontSize );
    }

    /** callback for hover over count/percent text element */
    overText( event ) {
        var hasSelectableCategory = Boolean(this.data("category"));
        if( hasSelectableCategory ) {
            event.currentTarget.style.cursor = Dyn.plotUtil.cursorPointer;
        }
        Dyn.toolTip.show( this, event, { x:this.attrs.x, y:this.attrs.y });
    }

    /** callback for outgoing hover over count/percent text element */
    outText( event ) {
        if( Dyn.toolTip.isMyElement( this )) {
            Dyn.toolTip.hide();
        }
        event.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
    }

    /**
     * Get the decimal precision for our Count/Sum/Mean/Percent/Proportion display
     * @param attribute
     * @param options -- for options.isMean
     * @returns {number|undefined}
     */
    getPrecision( attribute, options ) {
        const significantFiguresDelta = 1; // +1 for averages, from "Significant Figures - Rules & in Tuva" from Kristian Hunter-Thomson
        return (options && options.isMean) ?
            // for bar of means display, use same decimal precision as AverageElementBase.getPrecision()
            attribute.getNumericDecimals() + significantFiguresDelta + Dyn.preferences.getPrecision() :'' +
            undefined; // else caller defines precision
    }

    /**
     * Convert (count/sum/mean) and/or (percent/proportion) to the display string, at the proper precision.
     * @param {number} countOrStatistic - finite count value or NaN if no count should be displayed
     * @param {number} percent - finite percent value or NaN if no percent or proportion should be displayed
     * @param {string} attributeID - optional ID of the attribute used to format the numbers, if any.
     * @param {wantProportion,wantPercent} options - options like options.wantProportion=true
     * @returns {string}
     */
    countOrPercentNumberToText( countOrStatistic, percent, attributeID, options ) {
        let countString,
            percentString;

        if( isFinite( countOrStatistic )) {
            // get countString for a Count, Mean, or Sum
            countOrStatistic = Dyn.mathUtil.fixPrecision( countOrStatistic ); // #1664
            let attribute = attributeID ? this.plotShaper.dataSet.getAttributeByID( attributeID ) : null;
            if( attribute ) {
                countString = attribute.axisValueToString( countOrStatistic, this.getPrecision( attribute, options ));
            } else {
                let countDecimals = Dyn.preferences.getPrecision(); // default 0
                countString = Dyn.mathUtil.roundIfInteger(Dyn.mathUtil.roundToDecimalPlaces(countOrStatistic, countDecimals)).toString();
            }
        }
        if( isFinite( percent )) {
            // get percentString for Percent or Proportion (or both), possibly combined with the above countString (Count/Mean/Sum)
            percent = Dyn.mathUtil.fixPrecision( percent ); // #1664
            let wantProportion = options && options.wantProportion,
                wantPercent = (options && options.wantPercent) || ! wantProportion;

            function percentToPercentString( percent, decimals ) {
                return Dyn.mathUtil.roundIfInteger( Dyn.mathUtil.roundToDecimalPlaces( percent, decimals )).toString();
            }
            function percentToProportionString( percent, decimals ) {
                return Dyn.mathUtil.roundIfInteger( Dyn.mathUtil.roundToDecimalPlaces( percent/100, decimals )).toString();
            }

            // precision for the percentage
            let fixedDecimals = 1;
            if(      percent >= 0.1  ) { fixedDecimals = 1; } // round values to 1 places after decimal, except
            else if( percent < 0.1  )  { fixedDecimals = 2; } // show 0.09-0.01 instead of 0.0
            else if( percent < 0.01 )  { fixedDecimals = 3; } // show 0.009-0.001 instead of 0.00
            else if( percent < 0.001 ) { fixedDecimals = 4; } // show 0.0009-0.0001 instead of 0.000
            // add user preference for +/- precision and clip to 0+
            fixedDecimals += Dyn.preferences.getPrecision()
            let proportionDecimals = fixedDecimals+2; // e.g. 12.3% as percent, or 0.123 as a proportion

            // get percent or proportion or both
            if( wantPercent && wantProportion ) {
                percentString = Dyn.loc("{{percent}}%={{proportion}}", {
                    percent: percentToPercentString( percent, fixedDecimals ),
                    proportion: percentToProportionString( percent, proportionDecimals )
                });
            } else if( wantProportion ) {
                percentString = Dyn.loc("{{proportion}}", {
                    proportion: percentToProportionString( percent, proportionDecimals )
                });
            } else {
                percentString = Dyn.loc("{{percent}}%", {
                    percent: percentToPercentString( percent, fixedDecimals )
                });
            }

            // combine with countString
            if( countString ) {
                percentString = Dyn.loc( "{{count}} ({{percent}})", { count:countString, percent:percentString } );
            } else {
                percentString = Dyn.loc( "{{percent}}", { percent:percentString } );
            }
            return percentString;	// showing percent with or without countOrStatistic
        }
        Dyn.assert( countString, "missing countOrStatistic value" );
        return countString;			// showing countOrStatistic alone
    }

    /**
     * Convert count or percent or both to the tooltip string, at the proper precision.
     * @param {number} countOrStatistic - finite count value or NaN if no count should be displayed
     * @param {number} percent - finite percent value or NaN if no percent should be displayed
     * @param {Dyn.Attribute.id} attributeID - optional attribute ID string, to format countOrStatistic
     * @param {{isSum,isMean,wantProportion,wantPercent}} options -- type of statistic options.isSum or options.isMean
     * @returns {string}
     */
    countOrPercentNumberToToolTip( countOrStatistic, percent, attributeID, options ) {
        // round values to 2 places after decimal, e.g. show 0.127 as 0.13%, 15.000 as 15%
        var valueString,
            countString,
            percentString,
            attribute = attributeID ? this.plotShaper.dataSet.getAttributeByID( attributeID ) : null,
            attributeName = attribute ? attribute.name : "";

        if( isFinite( countOrStatistic )) {
            // get countString for a Count, Mean, or Sum
            countOrStatistic = Dyn.mathUtil.fixPrecision( countOrStatistic ); // #1664
            if( attribute ) {
                valueString = attribute.axisValueToString( countOrStatistic, this.getPrecision( attribute, options ));
            } else {
                let countDecimals = Dyn.preferences.getPrecision();
                valueString = Dyn.mathUtil.roundIfInteger( Dyn.mathUtil.roundToDecimalPlaces( countOrStatistic, countDecimals )).toString();
            }
            let statName = "";
            if( options && ( options.isSum || options.isMean )) {
                if( ! attribute ) {
                    attributeName = Dyn.loc( "multiple attributes" );
                }
                statName = Dyn.loc( options.isMean ? "Mean" : "Sum" );
                //countString = "Sum of {{count}} for {{attribute}}"; // old string pre 2.4.0
                //countString = "Sum of {{count}} for multiple attributes"; // old string pre 2.4.0
                countString = "{{statistic}} of {{count}} for {{attribute}}";
            } else {
                countString = "{{count}} {{cases}}";
            }
            countString = Dyn.loc( countString, {
                count: valueString, attribute: attributeName, cases:((countOrStatistic===1)?"case":"cases"), statistic: statName  });
        }
        if( isFinite( percent )) {
            // get percentString for Percent or Proportion (or both), possibly combined with the above countString (Count/Mean/Sum)
            percent = Dyn.mathUtil.fixPrecision( percent ); // #1664
            const fixedDecimals = 2;
            let wantProportion = options && options.wantProportion,
                wantPercent = (options && options.wantPercent) || ! wantProportion;
            if( wantPercent && wantProportion ) {
                valueString = Dyn.loc("{{percent}}%={{proportion}}", {
                    percent: Dyn.mathUtil.roundIfInteger( percent.toFixed( fixedDecimals )),
                    proportion: Dyn.mathUtil.roundIfInteger( (percent/100).toFixed( fixedDecimals+2 ))
                });
            } else if( wantProportion ) {
                valueString = Dyn.loc("{{proportion}}", {
                    proportion: Dyn.mathUtil.roundIfInteger( (percent/100).toFixed( fixedDecimals+2 ))
                });
            } else {
                valueString = Dyn.loc("{{percent}}%", {
                    percent: Dyn.mathUtil.roundIfInteger( percent.toFixed( fixedDecimals )),
                    proportion: Dyn.mathUtil.roundIfInteger( (percent/100).toFixed( fixedDecimals+2 ))
                });
            }

            let stringParams = { count:countString, percent:valueString, attribute:attributeName };
            if( countString ) {
                //percentString = "{{count}} ({{percent}}%)" // old string pre 2.11.0
                percentString = Dyn.loc( "{{count}} ({{percent}})", stringParams );
            } else {
                if( attribute && options && ( options.isSum || options.isMean )) {
                    //percentString = "{{percent}}% for {{attribute}}" // old string pre 2.11.0
                    percentString = Dyn.loc( "{{percent}} for {{attribute}}", stringParams );
                } else {
                    //percentString = "{{percent}}%"; // old string pre 2.4.0
                    //percentString = "{{percent}}% of cases" // old string pre 2.11.0
                    percentString = Dyn.loc( "{{percent}} of cases", stringParams );
                }
                // percentString = Dyn.loc( percentString, {
                //     percent:valueString, attribute: attributeName } );
            }
            return percentString;	// showing percent with or without countOrStatistic
        }
        Dyn.assert( countString, "missing countOrStatistic value" );
        return countString;
    }

    /**
     * Add adornments which display text % of cells in any plot.
     * Requires cell.cases.length calculated by Dyn.PlotElement.populateCellsWithCases()
     * Updates plotViewPaper and adornments.
     */
    addCellCountOrPercent( adornments, plotCells, wantCounts, wantPercents, noAnimation ) {
        var plotViewPaper = this.plotShaper.adornments.paper,
            plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
            numCells = plotCells.length,
            textMargin = this.kCellMarginRoomy,
            maxTextWidth = 0,
            maxTextHeight = 0,
            maxCellWidth = 0,
            maxCellHeight = 0,
            animationDelay = noAnimation ? 0 : Dyn.plotUtil.textAdornAnimationTimeMs,
            i, cell, textElem, percentName;

        if( this.isTwoWayTable()) {
            if( this.showRowPercents ) {
                percentName = Dyn.loc("Percent (Row-wise)");
            } else if ( this.showColPercents ) {
                percentName = Dyn.loc("Percent (Column-wise)");
            } else {
                percentName = Dyn.loc("Overall Percent");
            }
        } else {
            percentName = Dyn.loc("Percent");
        }

        // iterate over the plotcells and add the case count element to the plot
        for( i=0; i<numCells; i++ ) {
            cell      = plotCells[i];
            let count     = wantCounts ? cell.cases.length : NaN;
            let percent   = wantPercents ? cell.percent : NaN;
            let textValue = this.countOrPercentNumberToText( count, percent );

            textElem = plotViewPaper.text( cell.x + ( cell.width - textMargin ), ( cell.y + textMargin ), textValue );
            textElem.attr( {opacity: 0, "font-size": this.getCellFontSize( cell, "CellCountOrPercent" )} );
            textElem.attr( {"transform": Dyn.plotUtil.getTextTransform( textElem, "right,top" )} );
            textElem.animate( {opacity: 1}, animationDelay, Dyn.plotUtil.textAdornAnimationEasing );
            textElem.data( "tooltip", this.countOrPercentNumberToToolTip( count, percent ) );
            textElem.hover( this.overText, this.outText );
            adornments.push( textElem );

            // check widths;
            let bbox = textElem.getBBox( Dyn.plotUtil.textBBoxBeforeTransform );
            maxTextWidth  = Math.max( maxTextWidth,  bbox.width );
            maxTextHeight = Math.max( maxTextHeight, bbox.height );
            maxCellWidth  = Math.max( maxCellWidth,  plotCells[i].width );
            maxCellHeight = Math.max( maxCellHeight, plotCells[i].height );

            if( wantCounts ) {
                plotMeasures.addToMeasuresArray( measuresArray, {
                    setID: "cell",
                    measureName: Dyn.loc( "Count" ),
                    value: this.countOrPercentNumberToText( count, NaN )
                    //ignoreAttributeID: true // count is same if 0, 1, or 2+ attributes on axis (unless attribute creates bins)
                }, cell.xBinIndex, cell.yBinIndex );
            }
            if( wantPercents ) {
                plotMeasures.addToMeasuresArray( measuresArray, {
                    setID: "cell",
                    measureName: percentName,
                    value: this.countOrPercentNumberToText( NaN, percent )
                    //ignoreAttributeID: true // count is same if 0, 1, or 2+ attributes on axis (unless attribute creates bins)
                }, cell.xBinIndex, cell.yBinIndex );
            }
        }

        // if text too wide for top-right-justified layout, then reduce margin, clip, and center it in X or Y or both
        let isTooBigX = ( (maxTextWidth + (2*textMargin)) > maxCellWidth ),
            isTooBigY = ( (maxTextHeight + (2*textMargin)) > maxCellHeight );
        if( isTooBigX || isTooBigY ) {
            textMargin = this.kCellMarginTight; // reduce margin
            maxCellWidth -= textMargin * 2;
            maxCellHeight -= textMargin * 2;
            for( i=0; i<numCells; i++ ) {
                cell     = plotCells[i];
                textElem = adornments[i + adornments.length - numCells]; // get text adorments just pushed above
                if( isTooBigX && !isTooBigY ) {
                    // text too big in X for cell: clip text and center in X instead of default right-justfy
                    Dyn.plotUtil.clipStringToWidth( textElem, maxCellWidth );
                    textElem.attr( {
                        "transform": Dyn.plotUtil.getTextTransform( textElem, "center,top" ),
                        x: cell.x + cell.width / 2 // x center point
                    } );
                } else if( isTooBigY && !isTooBigX ) {
                    // text too big in Y for cell, so...
                    if( maxTextHeight > maxCellHeight ) {    // check again with smaller margin
                        textElem.attr( "text", "..." ); // not enough height; clip all of it and make user use tooltip to see text
                    }
                    textElem.attr( { // center in Y
                        "transform": Dyn.plotUtil.getTextTransform( textElem, "right,center" ),
                        y: cell.y + cell.height / 2 // y center point
                    } );
                } else {
                    // text too big in both dimensions (very small cells), so...
                    if( maxTextHeight > maxCellHeight ) {    // check again with smaller margin
                        textElem.attr( "text", "..." ); // not enough height; clip all of it and make user use tooltip to see text
                    }
                    textElem.attr( { // tiny cell in both dimensions, so center text even if it means overlap with data
                        "transform": Dyn.plotUtil.getTextTransform( textElem, "center,center" ),
                        x: cell.x + cell.width / 2, // x center point
                        y: cell.y + cell.height / 2
                    } );
                }
            }
        }
        // reserve room for cell count/percent unless cell is so small that we need to overlap
        this.cellMarginY = (( maxTextHeight > maxCellHeight/2 ) ? 0 : (maxTextHeight + textMargin));
        this.cellMarginX = (( maxTextWidth  > maxCellWidth /2 ) ? 0 : (maxTextWidth  + textMargin));
        //console.log("CountPercentDividersElementBase.addCellCountOrPercent() cell margins in X and Y :", this.cellMarginX, this.cellMarginY );

        plotMeasures.setMissingValueForMeasure( Dyn.loc("Count"), "-" );
        plotMeasures.setMissingValueForMeasure( percentName, "-" );
    }

    /**
     * Add adornments which display text % of sections in each pie, one per cell.
     * Requires cell.pieSections calculated by Dyn.PieElement()
     * Updates plotViewPaper and adornments.
     */
    addPieSectionCountOrPercent( adornments, plotCells, wantCounts, wantPercents, noAnimation ) {
        var plotViewPaper = this.plotShaper.adornments.paper,
            plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
            numCells = plotCells.length,
            animationDelay = noAnimation ? 0 : Dyn.plotUtil.textAdornAnimationTimeMs,
            wantTextToolTip = false, //! this.plotShaper.hasElement( Dyn.EPlotControl.kControlsMultiCaseSections ),
            i, j, cell;

        // for 1+ cells in plot
        for( i=0; i<numCells; i++ ){
            cell = plotCells[i];

            // for 0+ pie sections in cell
            for( j=0; j<cell.pieSections.length; j++ ) {
                var section = cell.pieSections[j],
                    sectionValue = section.isSum ? section.valueSum : section.caseCount, // may be count or sum of numeric attribute values for section
                    countOrSum = wantCounts ? sectionValue : NaN, // may be case count, or sum of numeric attribute values, or NaN
                    //sum = wantCounts ? section.valueSum : NaN, // may be count or sum of numeric attribute values for section
                    percent = wantPercents ? (100 * (section.arcLength / Dyn.mathUtil.kTwoPi)) : NaN,
                    textValue = this.countOrPercentNumberToText( countOrSum, percent ),
                    tipText = this.countOrPercentNumberToToolTip( countOrSum, percent, section.valueAttributeID, section ),
                    textElem = plotViewPaper.
                        text( section.x, section.y, textValue ).
                        attr({ opacity:0, fill:section.textColor, "font-size":this.getPieSectionFontSize( section ) } ),
                    categoryIndex = plotMeasures.cBinNames.indexOf( section.category );

                section.countPercentTip = tipText; // save for PieElement.getExtraTooltipForCase()
                this.adjustPieSectionLabelPlacement( section, textElem );

                textElem.animate({ opacity:1 }, animationDelay, Dyn.plotUtil.textAdornAnimationEasing ).
                    data( "attributeID", section.categoryAttributeID ).
                    data( "category", section.category ).
                    data( "value", section.arcLength ).
                    data( "max", Dyn.mathUtil.kTwoPi ).
                    data( "cell:section", i+":"+j ).
                    data( "tooltip", tipText );
                if( wantTextToolTip ) {
                    // NOTE: DISABLED, because it is better to click on entire slice/section, not just on the text value.
                    textElem.hover( this.overText, this.outText )
                        .click( this.clickCategory );
                } else {
                    Dyn.plotUtil.disableMouseOverEvents( textElem );  // only use case tool tip
                }
                adornments.push( textElem );

                // save measures for Summary View table
                if( wantCounts ) {
                    addPieMeasure(
                        Dyn.loc( section.isSum ? "Sum":"Count" ),
                        this.countOrPercentNumberToText( countOrSum, NaN ),
                        section.valueAttributeID,
                        cell,
                        categoryIndex );
                }
                if( wantPercents ) {
                    addPieMeasure(
                        Dyn.loc( "Percent" ),
                        this.countOrPercentNumberToText( NaN, percent ),
                        section.valueAttributeID,
                        cell,
                        categoryIndex );
                }
            }
            plotMeasures.setMissingValueForMeasure( Dyn.loc("Count"), "0" );
            plotMeasures.setMissingValueForMeasure( Dyn.loc("Sum"), "0" );
            plotMeasures.setMissingValueForMeasure( Dyn.loc("Percent"), "0%" );
        }
        function addPieMeasure( measureName, value, attributeID, cell, categoryIndex ) {
            var measureData = {
                    setID: 'pie',
                    measureName: measureName,
                    value: value
            };
            if( attributeID ) {
                measureData.attributeID = attributeID;
            }
            plotMeasures.addToMeasuresArray( measuresArray, measureData, cell.xBinIndex, cell.yBinIndex, categoryIndex );
        }
    }

    /**
     * With the pie section label placed horizontally, check its size, and if too long to fit inside the section,
     * rotate the label, so it fits better (along to the section bisector).
     * @param section -- section to be examined
     * @param textElem -- text element for label, which we may modify
     */
    adjustPieSectionLabelPlacement( section, textElem ) {

        function sectionMidAngleToTextRotationInDegrees( angleInRadians, quadrant ) {
            let rotationDegrees = Math.round( angleInRadians / Math.PI * 180);
            if( quadrant === 2 || quadrant === 3 ) { // quadrant 0=upper-right, 1=lower-right, 2=lower-left, 3=upper-left
                rotationDegrees -= 180;
            }
            return rotationDegrees;
        }
        function isSliceLessThanOrEqualToHalfCircle( angleInRadians ) {
            return( angleInRadians <= Math.PI );
        }
        function isTextNotTooLongToFit( textWidth, radius ) {
            return( textWidth <= radius );
        }

        // first shrink text that is too long
        let isFullCircle = Dyn.mathUtil.isClose( section.arcLength, Dyn.mathUtil.kTwoPi ),
            isHalfOrLess = isSliceLessThanOrEqualToHalfCircle( section.arcLength ),
            effectiveRadius = section.r * (isFullCircle ? 2 : 1 ),
            textBBox, isTextNotTooLong, fontSize;
        const kMinFontSize = 4;
        do {
            if( fontSize ) { // just get sizes first time through
                // SHRINK text and recalculate
                fontSize = Math.max( kMinFontSize, Math.round( fontSize * 0.5 ));
                textElem.attr( "font-size", fontSize );
            }
            fontSize = textElem.attr("font-size");
            textBBox = textElem.getBBox();
            isTextNotTooLong = isTextNotTooLongToFit( textBBox.width, effectiveRadius );
        } while( fontSize > CountPercentDividersElementBase.minPieSectionFontSize && ! isTextNotTooLong )

        // Does label fit better if rotated to be perpendicular to slice midline?
        if( isHalfOrLess && isTextNotTooLong ) {
            // compute angles of corners of text element bbox and determine if any are
            let iconStyler = this.plotShaper.iconStyler,
                sliceAngles = iconStyler.calcPieSliceAngles( section.arcStart, section.arcLength ),
                textElemCorners = [
                    [ textBBox.x, textBBox.y ],
                    [ textBBox.x, textBBox.y2 ],
                    [ textBBox.x2, textBBox.y ],
                    [ textBBox.x2, textBBox.y2 ]
                ],
                textOutsideOfArc = false;
            textElemCorners.forEach( (corner) => {
                let angle = iconStyler.calcAngleOfPieSlicePoint( corner[0], corner[1], section.cx, section.cy, sliceAngles.angleMidQuadrant );
                if( angle < sliceAngles.angle1 || angle > sliceAngles.angle2 ) {
                    textOutsideOfArc = true;
                    //console.log( "section angle NOT in arc", sliceAngles.angle1, angle, sliceAngles.angle2, sliceAngles.angleMidQuadrant );
                }
            });
            if( textOutsideOfArc ) {
                let rotationDegrees = sectionMidAngleToTextRotationInDegrees( sliceAngles.angleMid, sliceAngles.angleMidQuadrant );
                textElem.transform( "r"+rotationDegrees );  // ROTATE the text from the horizontal
                //console.log( "adjustPieSectionLabelPlacement rotate label", textElem.attr( "text" ), "r"+sliceAngles.angleMid );
            }
        }
        else if( isFullCircle ) {
            // if 360 degree section, CENTER the label horizontally in the circle
            textElem.attr({ x: section.cx, y: section.cy });
        }
    }

    /**
     * Add adornments which display text Count, Sum, and/or Percent of sections in the bar chart.
     * Requires cell.barSections and cell.barData calculated by Dyn.BarElement()
     * Updates plotViewPaper and adornments.
     */
    addBarSectionCountOrPercent( adornments, plotCells, wantCounts, wantPercents, noAnimation ) {
        var plotViewPaper = this.plotShaper.adornments.paper,
            plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
            numCells = plotCells.length,
            animationDelay = noAnimation ? 0 : Dyn.plotUtil.textAdornAnimationTimeMs,
            wantTextToolTip = ! this.plotShaper.hasElement( Dyn.EPlotControl.kControlsMultiCaseSections );

        // for 1+ cells in plot
        for( let i=0; i<numCells; i++ ) {
            let cell = plotCells[i],
                isHorizontal = cell.barData.stackHorizontal;

            // for 0+ bar sections in cell
            for( let j=0; j<cell.barSections.length; j++ ) {
                let section = cell.barSections[j],
                    overallValue = this.getBarValue( section, cell.barData.valueSum, cell.barData.caseCount ),
                    sectionValue = this.getBarValue( section, section.valueSum, section.caseCount ),
                    countSumMean = wantCounts ? sectionValue : NaN,// may be case count, or sum of numeric attribute values, or NaN
                    percent = wantPercents ? (100 * ( sectionValue * Dyn.mathUtil.invertSafely( overallValue ))) : NaN,
                    tipText = this.countOrPercentNumberToToolTip( countSumMean, percent, section.valueAttributeID, section ),
                    textValue = this.countOrPercentNumberToText( countSumMean, percent, section.valueAttributeID, section ),
                    fontSize = this.getBarFontSize( section.rect, "Bar Section" ),
                    textElem = makeText( section, textValue, fontSize, isHorizontal ),
                    textBBox = textElem.getBBox( Dyn.plotUtil.textBBoxAfterTransform ),
                    categoryIndex = plotMeasures.cBinNames.indexOf( section.category );

                categoryIndex = (categoryIndex >= 0) ? categoryIndex : undefined; // undefined if not found for addToMeasuresArray()
                section.countPercentTip = tipText; // save for BarElement.getExtraTooltipForCase()

                // create text adornment to show count/sum/percent in plot
                if( textBBox.width >= section.rect.width || textBBox.height >= section.rect.height ) {
                    // elide text if not text size smaller than section size in either dimension
                    textElem.remove();
                    textElem = makeText( section, "...", fontSize, isHorizontal );
                    // to center the "...", shift text up 30% of font size; this is pre-rotation so works for horizontal or vertical text
                    const elideRecenter = fontSize*0.3;
                    textElem.attr( "y", textElem.attr("y") - elideRecenter );
                }
                textElem.animate( {opacity:1}, animationDelay, Dyn.plotUtil.textAdornAnimationEasing ).
                    data( "attributeID", section.categoryAttributeID ).
                    data( "category", section.category ).
                    data( "value", sectionValue ).
                    data( "max", overallValue ). // TODO: change to max value, not sum of values
                    data( "cell:section", i+":"+j ).
                    data( "tooltip", tipText ).
                    click( this.clickCategory );
                if( wantTextToolTip ) {
                    textElem.hover( this.overText, this.outText );
                } else {
                    Dyn.plotUtil.disableMouseOverEvents( textElem ); // only use case tool tip
                }
                adornments.push( textElem );

                // save measures for Summary View table
                let isDuplicateMeanMeasure = section.isMean && this.plotShaper.hasElement( Dyn.EPlotControl.kControlsMean ),
                    textValueIsCountOnly = (wantCounts && ! wantPercents),
                    textValueIsPercentOnly = (wantPercents && ! wantCounts );
                if( wantCounts && ! isDuplicateMeanMeasure ) {
                    addBarSectionMeasure(
                        this.getBarValueName( section ),
                        (textValueIsCountOnly ? textValue :
                            this.countOrPercentNumberToText( countSumMean, NaN, section.valueAttributeID, section )),
                        section.valueAttributeID,
                        cell,
                        categoryIndex );
                }
                if( wantPercents ) {
                    addBarSectionMeasure(
                        Dyn.loc( "Percent" ),
                        (textValueIsPercentOnly ? textValue :
                            this.countOrPercentNumberToText( NaN, percent, section.valueAttributeID, section )),
                        section.valueAttributeID,
                        cell,
                        categoryIndex );
                }
            }
        }
        plotMeasures.setMissingValueForMeasure( Dyn.loc("Count"), "0" );
        plotMeasures.setMissingValueForMeasure( Dyn.loc("Sum"), "0" );
        plotMeasures.setMissingValueForMeasure( Dyn.loc("Percent"), "0%" );

        function makeText( section, textValue, fontSize, isHorizontal ) {
            var element = plotViewPaper.text( section.rect.centerX(), section.rect.centerY(), textValue );
            element.attr({ "font-size": fontSize, opacity:0, fill:section.textColor });
            if( ! isHorizontal ) {
                element.attr( "transform", "r90" );
            }
            return element;
        }
        function addBarSectionMeasure( measureName, value, attributeID, cell, categoryIndex ) {
            var measureData = {
                    setID: 'bar',
                    measureName: measureName,
                    value: value
                };
            if( attributeID ) {
                measureData.attributeID = attributeID;
            }
            plotMeasures.addToMeasuresArray( measuresArray, measureData, cell.xBinIndex, cell.yBinIndex, categoryIndex );
        }
    }

    /**
     * Add adornments which display text Count, Percent, and/or Proportion of individual bars/sections that make up the histogram(s).
     * Requires cell.histogramData calculated by Dyn.HistoElement()
     * Updates plotViewPaper and adornments.
     */
    addHistogramCountPercentProportion( adornments, plotCells, /*wantCounts, wantPercents,*/ noAnimation ) {
        var plotViewPaper = this.plotShaper.adornments.paper,
            plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
            numCells = plotCells.length,
            animationDelay = noAnimation ? 0 : Dyn.plotUtil.textAdornAnimationTimeMs,
            //animationEasing = Dyn.plotUtil.textAdornAnimationEasing,
            wantTextToolTip = true, //! this.plotShaper.hasElement( Dyn.EPlotControl.kControlsMultiCaseSections ),
            i, j, cell, isHorizontal;
        let wantCounts = Boolean( this.showHistogramCounts ),
            wantPercents = Boolean( this.showHistogramPercents ), // undefined=>false for CountElement
            wantProportions = Boolean( this.showHistogramProportions ), // undefined=>false for CountElement
            textOptions = { wantProportion:wantProportions, wantPercent:wantPercents }; // percent or proportion or both?

        // for 1+ cells in plot
        for( i=0; i<numCells; i++ ) {
            cell = plotCells[i];
            isHorizontal = cell.histogramData.stackHorizontal;

            // for 0+ bar sections in cell
            for( j=0; j<cell.histogramData.barEnds.length; j++ ) {
                // create text adornment to show count/sum/percent in plot
                let barRect = cell.histogramData.barEnds[j],
                    totalCountInCell = cell.cases.length,
                    count = wantCounts ? barRect.caseCount : NaN,
                    percent = (wantPercents || wantProportions) ? (100 * ( barRect.caseCount * Dyn.mathUtil.invertSafely( totalCountInCell ))) : NaN,
                    tipText = this.countOrPercentNumberToToolTip( count, percent, barRect.valueAttributeID, textOptions ),
                    textValue = this.countOrPercentNumberToText( count, percent, "", textOptions ),
                    fontSize = this.getHistogramFontSize( barRect, isHorizontal ),
                    textRectSize = 2.0 * fontSize, // add some whitespace around text
                    textRect = isHorizontal ?
                        new Dyn.mathUtil.Rect( barRect.left(), barRect.top() - textRectSize, barRect.width, textRectSize ) :
                        new Dyn.mathUtil.Rect( barRect.right(), barRect.top(), textRectSize, barRect.height ),
                    textElem = makeText( textRect, textValue, fontSize, isHorizontal ),
                    textBBox = Dyn.plotUtil.clipStringToWidth( textElem, barRect.widthOrHeight( isHorizontal ));

                barRect.countPercentTip = tipText; // TODO save for HistoElement.getExtraTooltipForCase()

                // if( isHorizontal ? textBBox.width > (2*barRect.width) : textBBox.height > (2*barRect.height) ) {
                //     // elide text if not enough room to display it
                //     textElem.remove();
                //     textElem = makeText( textRect, "...", fontSize, isHorizontal );
                // }
                if( barRect.caseCount === 0 ) {
                    textElem.attr("text",""); // hide count/percents if 0 cases
                }
                textElem.animate( {opacity:1}, animationDelay, Dyn.plotUtil.textAdornAnimationEasing ).
                    data( "attributeID", cell.histogramData.valueAttributeID ).
                    data( "interval", barRect.intervalString ).
                    data( "value", barRect.caseCount ).
                    data( "max", cell.histogramData.maxStackCount ).
                    data( "tooltip", tipText ).
                    click( this.clickInterval );
                if( wantTextToolTip ) {
                    textElem.hover( this.overText, this.outText );
                } else {
                    Dyn.plotUtil.disableMouseOverEvents( textElem ); // only use case tool tip
                }
                adornments.push( textElem );

                // save measures for Summary View table
                if( wantCounts ) {
                    plotMeasures.addToMeasuresArray( measuresArray, {
                        setID: "histo",
                        measureName: Dyn.loc( "Count" ),
                        value: this.countOrPercentNumberToText( count, NaN ),
                        histoBinIndex: j
                    }, cell.xBinIndex, cell.yBinIndex /*, categoryIndex */);
                }
                if( wantPercents ) {
                    plotMeasures.addToMeasuresArray( measuresArray, {
                        setID: "histo",
                        measureName: Dyn.loc( "Percent" ),
                        value: this.countOrPercentNumberToText( NaN, percent ),
                        histoBinIndex: j
                    }, cell.xBinIndex, cell.yBinIndex /*, categoryIndex */ );
                }
                if( wantProportions ) {
                    plotMeasures.addToMeasuresArray( measuresArray, {
                        setID: "histo",
                        measureName: Dyn.loc( "Proportion" ),
                        value: this.countOrPercentNumberToText( NaN, percent, "", { wantProportion:true } ),
                        histoBinIndex: j
                    }, cell.xBinIndex, cell.yBinIndex /*, categoryIndex */ );
                }
            }
        }
        plotMeasures.setMissingValueForMeasure( Dyn.loc("Count"), "0" );
        plotMeasures.setMissingValueForMeasure( Dyn.loc("Percent"), "0%" );
        plotMeasures.setMissingValueForMeasure( Dyn.loc("Proportion"), "0" );

        function makeText( barRect, textValue, fontSize, isHorizontal ) {
            var element = plotViewPaper.text( barRect.centerX(), barRect.centerY(), textValue );
            element.attr({ "font-size":fontSize, opacity:0 });
            if( ! isHorizontal ) {
                element.attr( "transform", "r90" );
            }
            return element;
        }
    }

    /**
     * Add adornments which display text of the total count/sum of each stacked or stretched bar of bar chart, one per bar.
     * Requires cell.barSections and cell.barData calculated by Dyn.BarElement()
     * Updates plotViewPaper and percentageAdorns.
     */
    addBarTotals( adornments, plotCells, noAnimation ) {
        var plotViewPaper = this.plotShaper.adornments.paper,
            plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
            numCells = plotCells.length,
            animationDelay = noAnimation ? 0 : Dyn.plotUtil.textAdornAnimationTimeMs;

        for ( let i=0; i<numCells; i++ ){
            let cell = plotCells[i];
            if( cell.barData ) {
                // center the sum text in the cell, above the end of the bar
                let barValue = this.getBarValue( cell.barData, cell.barData.valueSum, cell.barData.caseCount ),
                    valueAttributeID = cell.barData.valueAttributeID, //(cell.barSections.length > 0) ? cell.barSections[0].valueAttributeID : null,
                    isHorizontal = Boolean(cell.barData.stackHorizontal),
                    isReverseBar = Boolean(cell.barData.isReverseBar),
                    barEnd = cell.barData.barEnd,
                    fontSize = this.getBarFontSize( cell, "Bar Totals" ),
                    fontDirection = ( isHorizontal === isReverseBar ) ? 1 : -1,
                    barText = this.countOrPercentNumberToText( barValue, NaN, valueAttributeID ),
                    tipText = this.countOrPercentNumberToToolTip( barValue, NaN, valueAttributeID, cell.barData ),
                    textElem = plotViewPaper.text(
                        ( isHorizontal ? cell.centerX() : barEnd + (fontDirection*fontSize)),
                        ( isHorizontal ? barEnd + (fontDirection*fontSize) : cell.centerY() ),
                        barText );
                textElem.attr({ "font-size":fontSize, opacity:0 });
                if( ! isHorizontal ) {
                    textElem.attr( { "transform": "r90" } );
                }
                textElem.animate( {opacity:1}, animationDelay, Dyn.plotUtil.textAdornAnimationEasing );
                textElem.data( "tooltip", tipText );
                textElem.hover( this.overText, this.outText );
                adornments.push( textElem );

                // save measures for Summary View table
                addBarSectionMeasure(
                    this.getBarValueName( cell.barData ),
                    barText,
                    valueAttributeID,
                    cell
                    /* categoryIndex */
                );
            }
        }
        function addBarSectionMeasure( measureName, value, attributeID, cell, categoryIndex ) {
            var measureData = {
                    setID: 'bar',
                    measureName: measureName,
                    value: value
                };
            if( attributeID ) {
                measureData.attributeID = attributeID;
            }
            plotMeasures.addToMeasuresArray( measuresArray, measureData, cell.xBinIndex, cell.yBinIndex, categoryIndex );
        }
    }

    /** handler for clicking on Attribute Category */
    clickCategory( event ) {
        var attrID = this.data("attributeID" ),
            categoryName = this.data("category" ),
            value = this.data("value" ),
            max = this.data("max" ),
            wantAdd = Boolean( event && event.shiftKey ),
            data = { "attributeID":attrID, "category":categoryName, addToSelection:wantAdd };
        // play the data value for this category (e.g. pie slice width as value for category 'red'
        if( isFinite( value )) {
            Dyn.sound.playOneDataValue( value, 0, max );
        }
        // select the cases with the given category for this attribute
        mediator.publish( "records:casesSelectedByCategory", data );
    }

    /** handler for clicking on numeric interval for an attribute */
    clickInterval( event ) {
        var attrID = this.data("attributeID" ),
            intervalName = this.data("interval" ),
            value = this.data("value" ),
            max = this.data("max" );
        // in the future we can add selection of intervals for Summary View and Plot coordination
        //    wantAdd = Boolean( event && event.shiftKey ),
        //    data = { "attributeID":attrID, "category":categoryName, addToSelection:wantAdd };
        // select the cases with the given interval for this attribute
        //mediator.publish( "records:casesSelectedByInterval", data );
        Dyn.sound.playOneDataValue( value, 0, max );
    }

    getBarValue( cellOrSection, sum, count ) {
        let barValue = count;
        if( cellOrSection.isMean ) { // show mean instead of count
            barValue = Dyn.mathUtil.fixPrecision( sum * Dyn.mathUtil.invertSafely( count ));
        } else if( cellOrSection.isSum ) { // show sum instead of count
            barValue = Dyn.mathUtil.fixPrecision( sum );
        }
        return barValue;
    }

    getBarValueName( cellOrSection ) {
        return Dyn.loc( cellOrSection ? (cellOrSection.isMean ? "Mean":"Sum"):"Count" );
    }

    isTwoWayTable() {
        var numColumnBins = this.plotShaper.bins.xBins.length,
            numRowBins = this.plotShaper.bins.yBins.length;
        return( numColumnBins >= 2 && numRowBins >= 2 );
    }

    /** update the text colors that are on top of a particular section of a cell.
     * See addBarSectionCountOrPercent() and addPieSectionCountOrPercent() which creates text to find. */
    updateSectionTextColor( myAdornments, cellIndex, sectionIndex, textColor ) {
        const cellSectionID = cellIndex+":"+sectionIndex;
        myAdornments.forEach((element) => {
            if( element.type==="text" && element.data("cell:section")===cellSectionID ) {
                element.attr( "fill", textColor );
                //console.log("CountPercent:updateSectionTextColor", cellSectionID, textColor );
            }
        })
    }
} // class CountPercentDividersElementBase

//----------------------------------

/**
 * Dyn.CountElement()
 */
class CountElement extends CountPercentDividersElementBase {

    /**
     * @param [toggleControlID] {string|array} -- optional Dyn.EPlotControl settings to toggle on/off
     */
    constructor( Dyn, toggleControlID ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityCount,
            replaceType: Dyn.EPlotControl.kControlsCount,
            primaryType: Dyn.EPlotControl.kControlsCount,
            otherControls: [] // see toggleControlID and toggleControl() for other controls
        }, Dyn );
        // if provided a control type, or array of control types, only turn on that plot option
        if( toggleControlID ) {
            let controlsToTurnOn = Array.isArray( toggleControlID ) ? toggleControlID : [ toggleControlID ];
            // turn on the given controls, everything else off
            this[Dyn.EPlotControl.kControlsCellCount] = controlsToTurnOn.includes( Dyn.EPlotControl.kControlsCellCount );
            this[Dyn.EPlotControl.kControlsSectionCount] = controlsToTurnOn.includes( Dyn.EPlotControl.kControlsSectionCount );
            this[Dyn.EPlotControl.kControlsSectionSum] = controlsToTurnOn.includes( Dyn.EPlotControl.kControlsSectionSum );
        } else {
            // Default options to use, if no toggleControlID parameter
            this[Dyn.EPlotControl.kControlsCellCount] = true;
            this[Dyn.EPlotControl.kControlsSectionCount] = false;
            this[Dyn.EPlotControl.kControlsSectionSum] = false;
        }
        this[Dyn.EPlotControl.kControlsMargins] = this.isControllingMarginsOrAxes();

        // flags for which type of counts this element is currently displaying
        // if FALSE, count may be displayed by DividerElement or PercentageElement.
        this.showBinCounts = false;
        this.showPieCounts = false;
        this.showBarCounts = false;
        this.showHistogramCounts = false;
        this.showBarSums = false;
        this.showCountsWithPercents = false;
        this.suppressBinCountsPercents = false;
    }

    get kUndoAction() {
        return 'Count'
    }

    static onOffControls = [ // for toggleControl(), removeToggleControls()
        Dyn.EPlotControl.kControlsCellCount,
        Dyn.EPlotControl.kControlsSectionCount,
        Dyn.EPlotControl.kControlsSectionSum
    ];

    /** override PlotElement.save() to track toggleControl() options */
    save() {
        let plotElementModel = super.save();

        //plotElementModel.primaryType = this.primaryType;
        plotElementModel[Dyn.EPlotControl.kControlsCellCount] = this[Dyn.EPlotControl.kControlsCellCount];
        plotElementModel[Dyn.EPlotControl.kControlsSectionCount] = this[Dyn.EPlotControl.kControlsSectionCount];
        plotElementModel[Dyn.EPlotControl.kControlsSectionSum] = this[Dyn.EPlotControl.kControlsSectionSum];
        return plotElementModel;
    }

    // override PlotElement.restore() to track toggleControl() options
    restore( savedData ) {
        super.restore( savedData );

        // restore the reference line location
        this[Dyn.EPlotControl.kControlsCellCount] = Boolean( savedData[Dyn.EPlotControl.kControlsCellCount] );
        this[Dyn.EPlotControl.kControlsSectionCount] = Boolean( savedData[Dyn.EPlotControl.kControlsSectionCount] );
        this[Dyn.EPlotControl.kControlsSectionSum] = Boolean( savedData[Dyn.EPlotControl.kControlsSectionSum] );
        this[Dyn.EPlotControl.kControlsMargins] = this.isControllingMarginsOrAxes();
    }

    /**
     * Update our "which counts do we display" flags based on the plot configuration.
     * Designed to work no matter what the order that calcAdornments is called, so we don't
     * have to worry so much about order of evaluation.
     */
    calcShowFlags() {
        var showPercentages = this.plotShaper.hasElement( Dyn.EPlotControl.kControlsPercentage ),
            dividerElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsDividers ),
            pieElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsPieChart ),
            barElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsBarChart ),
            histoElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsHistogram ),
            isDividerDisplayed = dividerElement ? dividerElement.isDisplayingDividers() : false;

        this.showBinCounts = Boolean( this[Dyn.EPlotControl.kControlsCellCount] );
        this.showPieCounts = Boolean( this[Dyn.EPlotControl.kControlsSectionCount] && pieElement && pieElement.allowPieSectionCounts() );
        this.showBarCounts = Boolean( this[Dyn.EPlotControl.kControlsSectionCount] && barElement && barElement.allowBarSectionCounts( 'count' ) );
        this.showBarSums = Boolean( this[Dyn.EPlotControl.kControlsSectionSum] && barElement && barElement.allowBarSectionCounts( 'sum' ) );
        this.showHistogramCounts = Boolean( this[Dyn.EPlotControl.kControlsSectionCount] && histoElement && histoElement.allowHistogramSectionCounts() );

        // if showing pie chart sums (of some attribute), get the attribute use for formatting of sums
        //this.pieSumAttribute = pieElement && pieElement.getValueAttribute( Dyn.plotView.xAxis, Dyn.plotView.yAxis );

        // if Divider is on, so the calculations will be based on the dividers location and not the cell
        // if Percentage is on, Dyn.PercentageElement will display the counts in the same string as percents
        this.showCountsWithPercents = showPercentages;
        this.suppressBinCountsPercents = isDividerDisplayed;
    }

    /**
     * Do our current Count options require an adjustment to the plot axis or margin settings?
     */
    isControllingMarginsOrAxes() {
        // Turn on "kControlsMargins" when "kControlsSectionSum" or "kControlsSectionCount" is on.
        // Because Section Sums ("Bar Totals") take up space in the axis, unlike other types of count/sum,
        // and Because Histogram counts "Count by Histogram Section" also takes up space in the axis
        // PlotView.togglePlotElement() then knows to adjust the plot view layout when "kControlsSectionSum" is turned on and off.
        // note: The amount of space is calculated by BarElement.calcAxis(), and sent to axisView.marginForSum
        return Boolean(
            this[Dyn.EPlotControl.kControlsCellCount]
            || this[Dyn.EPlotControl.kControlsSectionSum]
            || this[Dyn.EPlotControl.kControlsSectionCount]
        );
    }

    /**
     * Modify the control type in this plot element, to turn on and off features.
     * Used by Dyn.PlotView.togglePlotElement().
     */
    toggleControl( toggleControlID ) {
        var controlValue = this[toggleControlID];
        Dyn.assert( typeof controlValue === "boolean", "toggleControl() could not find " + toggleControlID );

        // only modify control types on our list of allowable changes.
        if( CountElement.onOffControls.includes( toggleControlID ) ) {
            this[toggleControlID] = !controlValue;
        }
        this[Dyn.EPlotControl.kControlsMargins] = this.isControllingMarginsOrAxes();
    }

    /**
     * Return true if this element has all toggle elements turned off, and thus can be removed from the plot.
     * @returns {boolean}
     */
    isElementWithAllTogglesOff() {
        return !(
            this[Dyn.EPlotControl.kControlsCellCount] ||
            this[Dyn.EPlotControl.kControlsSectionCount] ||
            this[Dyn.EPlotControl.kControlsSectionSum]
        ); // true if no counts activated
    }

    /** How much space should axis reserve for text of counts? (reducing space for numeric axis, etc)
     * IMPORTANT: if there is a count and a percent, or count with Dividers, don't add space here,
     * instead let Dyn.PercentElement add space and draw the combined Count+Percent, or
     * let Dyn.Dividers add space and draw the count/percent in each division.
     * @returns {{x: (number), y: (number)}}
     */
    getCellMargin( /*whatChanged, subViewRef -- not used by callers, to be removed*/ ) {

        // crude function that just gets the largest text size instead of actually calculating sizes
        // because cell and text sizes are not available until they are positioned and placed
        // which is often too late for callers
        let representativeCell = new Dyn.mathUtil.Rect( 0, 0, 100, 100 ), // big enough to get largest size from getCellFontSize()
            fontSizeToHeight = 2, // centered text, so margin is font size * 2, see addBarTotals()
            estimatedTextHeight = fontSizeToHeight * this.getCellFontSize( representativeCell, "Count Cell Margin" );

        this.calcShowFlags(); // update flags like this.showBinCounts

        if( this.showBinCounts && !(this.showCountsWithPercents || this.suppressBinCountsPercents) ) {
            this.cellMarginY = this.cellMarginX = this.kCellMarginRoomy + estimatedTextHeight;
            //console.log( "CountElement.getCellMargin() count size="+this.cellMarginY);
        } else {
            this.cellMarginY = this.cellMarginX = 0;
        }
        if( this.showBarSums ) { // extra space for bar sums
            let barSumTextHeight = estimatedTextHeight;
            this.cellMarginY += barSumTextHeight;
            this.cellMarginX += barSumTextHeight;
            //console.log( "CountElement.getCellMargin() bar sum size="+this.cellMarginY);
        }
        if( this.showHistogramCounts && !this.showCountsWithPercents ) { // extra space for histogram counts (without percents)
            let histogramTextHeight = fontSizeToHeight * this.getHistogramFontSize( representativeCell );
            this.cellMarginY += histogramTextHeight;
            this.cellMarginX += histogramTextHeight;
            //console.log( "CountElement.getCellMargin() histogram count size="+this.cellMarginY);
        }

        return { x: this.cellMarginX, y: this.cellMarginY };
    }

    /**
     * Update the adornments array for the count element
     */
    calcAdornments( countsAdorns, iconPositions, xAxisView, yAxisView, noAnimation ) {
        // for each cell get number of cases
        var plotCells = this.plotShaper.bins.cells,
            plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType );

        this.calcShowFlags(); // update flags
        this.cellMarginY = this.cellMarginX = 0; // reset

        // remove old adornments and clear the adornments list
        Dyn.plotUtil.removeAndEmptyArrayOfElements( countsAdorns );
        measuresArray.length = 0; // remove old measures (values for summary table)

        if( this.showCountsWithPercents ) {
            return; // Dyn.PercentageElement will display the counts and percents in the same string
        }

        // add case count text elements to the plot adornments
        if( this.showBinCounts && !this.suppressBinCountsPercents ) {
            this.addCellCountOrPercent( countsAdorns, plotCells, this.showBinCounts, false /*no percents*/, noAnimation );
        }
        if( this.showPieCounts ) {
            this.addPieSectionCountOrPercent( countsAdorns, plotCells, true, false, noAnimation );
        }
        if( this.showBarCounts ) {
            // show bar counts (for stacked or stretched bar chart only)
            this.addBarSectionCountOrPercent( countsAdorns, plotCells, this.showBarCounts, false /*no percents*/, noAnimation );
        }
        if( this.showHistogramCounts ) {
            // show bar counts (for stacked or stretched bar chart only)
            this.addHistogramCountPercentProportion( countsAdorns, plotCells, /* this.showHistogramCounts, false,*/ noAnimation );
        }
        if( this.showBarSums ) {
            // show count/sum overall total at end of bar (for stacked or stretched bar chart only)
            this.addBarTotals( countsAdorns, plotCells, noAnimation );
        }
    }
} // class CountElement

Dyn.CountElement = CountElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsCount, Dyn.CountElement );

/**
 * Dyn.PercentageElement()
 * Plot Element that controls display of all percents and proportions, and all combinations
 * of count, percent, and proportion, EXCEPT for those that interact with dividers.
 * - See CountElement for count-only displays,
 * - See DividersElement for divider-related displays.
 */
class PercentageElement extends CountPercentDividersElementBase {

    /**
     * @param [toggleControlID] {string|array} -- optional Dyn.EPlotControl settings to toggle on/off
     */
    constructor( Dyn, toggleControlID ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityPercentage,
            replaceType: Dyn.EPlotControl.kControlsPercentage,
            primaryType: Dyn.EPlotControl.kControlsPercentage,
            otherControls: [
                Dyn.EPlotControl.kControlsCellPercentage, // do cell percentages by default, but changable
                Dyn.EPlotControl.kControlsSectionPercentage,	// check 'Bar/Pie Section Percent' menu item when parameter=true
                //Dyn.EPlotControl.kControlsAdornmentPositionOnly // NOTE: not possible with addition of this.getCellMargin()
                //Dyn.EPlotControl.kControlsMargins, // see isControllingMarginsOrAxes
            ] // for fast add/remove since icons/margins don't change
        }, Dyn );

        // if provided a control type, only turn on that plot option
        if( toggleControlID ) {
            // turn off all but the given control
            this[Dyn.EPlotControl.kControlsCellPercentage] = (toggleControlID === Dyn.EPlotControl.kControlsCellPercentage);
            this[Dyn.EPlotControl.kControlsRowPercentage] = (toggleControlID === Dyn.EPlotControl.kControlsRowPercentage);
            this[Dyn.EPlotControl.kControlsColPercentage] = (toggleControlID === Dyn.EPlotControl.kControlsColPercentage);
            this[Dyn.EPlotControl.kControlsSectionPercentage] = (toggleControlID === Dyn.EPlotControl.kControlsSectionPercentage);
            this[Dyn.EPlotControl.kControlsSectionProportion] = (toggleControlID === Dyn.EPlotControl.kControlsSectionProportion);
        } else {
            // explicitly turn these off by default
            this[Dyn.EPlotControl.kControlsRowPercentage] = false;
            this[Dyn.EPlotControl.kControlsColPercentage] = false;
            this[Dyn.EPlotControl.kControlsSectionProportion] = false;
        }
        this[Dyn.EPlotControl.kControlsMargins] = this.isControllingMarginsOrAxes();

        // flags for which counts/percents this element displays, updated by calcShowFlags()
        this.showBinCounts = false;
        this.showBinPercents = false;
        this.showPieCounts = false;
        this.showPiePercents = false;
        this.showBarCounts = false;
        this.showBarSums = false;
        this.showBarPercents = false;
        this.showHistogramCounts = false;
        this.showHistogramPercents = false;
        this.showHistogramProportions = false;
        this.showRowPercents = false;
        this.showColPercents = false;
        this.suppressBinCountsPercents = false;
    }

    get kUndoAction() {
        return 'Percentage';
    }

    static onOffControls = [ // for toggleControlID(), removeToggleControls()
        Dyn.EPlotControl.kControlsCellPercentage,
        Dyn.EPlotControl.kControlsRowPercentage,
        Dyn.EPlotControl.kControlsColPercentage,
        Dyn.EPlotControl.kControlsSectionPercentage,
        Dyn.EPlotControl.kControlsSectionProportion,
    ];

    /**
     * Save the PlotElement to a data object that can be JSONified.
     * @returns {{}} - data to be used by restore()
     */
    save() {
        var plotElementModel = super.save();

        // save toggle values
        plotElementModel[Dyn.EPlotControl.kControlsCellPercentage] = this[Dyn.EPlotControl.kControlsCellPercentage];
        plotElementModel[Dyn.EPlotControl.kControlsRowPercentage] = this[Dyn.EPlotControl.kControlsRowPercentage];
        plotElementModel[Dyn.EPlotControl.kControlsColPercentage] = this[Dyn.EPlotControl.kControlsColPercentage];
        plotElementModel[Dyn.EPlotControl.kControlsSectionPercentage] = this[Dyn.EPlotControl.kControlsSectionPercentage];
        plotElementModel[Dyn.EPlotControl.kControlsSectionProportion] = this[Dyn.EPlotControl.kControlsSectionProportion];

        return plotElementModel;
    }

    /**
     * Change our PlotElement to match the saved data.
     */
    restore( savedData ) {
        var thisElement = this;
        super.restore();

        switch( savedData.primaryType ) {
            case Dyn.EPlotControl.kControlsColPercentage:
            case Dyn.EPlotControl.kControlsRowPercentage:
            case Dyn.EPlotControl.kControlsCellPercentage:
                // restore older control types
                turnControlOn( savedData.primaryType );
                turnControlOn( Dyn.EPlotControl.kControlsSectionPercentage );
                break;
            // default including case Dyn.EPlotControl.kControlsPercentage:
            default:
                // restore newer toggleControl() flags
                this[Dyn.EPlotControl.kControlsCellPercentage] = Boolean( savedData[Dyn.EPlotControl.kControlsCellPercentage] );
                this[Dyn.EPlotControl.kControlsRowPercentage] = Boolean( savedData[Dyn.EPlotControl.kControlsRowPercentage] );
                this[Dyn.EPlotControl.kControlsColPercentage] = Boolean( savedData[Dyn.EPlotControl.kControlsColPercentage] );
                this[Dyn.EPlotControl.kControlsSectionPercentage] = Boolean( savedData[Dyn.EPlotControl.kControlsSectionPercentage] );
                this[Dyn.EPlotControl.kControlsSectionProportion] = Boolean( savedData[Dyn.EPlotControl.kControlsSectionProportion] );
                break;
        }

        this[Dyn.EPlotControl.kControlsMargins] = this.isControllingMarginsOrAxes();

        function turnControlOn( controlID ) {
            if( !thisElement[controlID] )
                thisElement.toggleControl( controlID );
        }
    }

    /**
     * Do our current Count options require an adjustment to the plot axis or margin settings?
     */
    isControllingMarginsOrAxes() {
        // Turn on "kControlsMargins" when "kControlsSectionSum" or "kControlsSectionCount" is on.
        // Because Section Sums ("Bar Totals") take up space in the axis, unlike other types of count/sum,
        // and Because Histogram counts "Count by Histogram Section" also takes up space in the axis
        // PlotView.togglePlotElement() then knows to adjust the plot view layout when "kControlsSectionSum" is turned on and off.
        // note: The amount of space is calculated by BarElement.calcAxis(), and sent to axisView.marginForSum
        return Boolean(
            this[Dyn.EPlotControl.kControlsCellPercentage]
            || this[Dyn.EPlotControl.kControlsRowPercentage]
            || this[Dyn.EPlotControl.kControlsColPercentage]
            || this[Dyn.EPlotControl.kControlsSectionPercentage]
            || this[Dyn.EPlotControl.kControlsSectionProportion]
        );
    }

    /**
     * Modify the control type in this plot element, to turn on and off features.
     * Used by Dyn.PlotView.togglePlotElement().
     */
    toggleControl( toggleControlID ) {
        var controlValue = this[toggleControlID];
        Dyn.assert( typeof controlValue === "boolean", "toggleControl() could not find " + toggleControlID );

        // only modify control types on our list of allowable changes.
        Dyn.assert( PercentageElement.onOffControls.includes( toggleControlID ) );
        switch( toggleControlID ) {
            case Dyn.EPlotControl.kControlsCellPercentage:
            case Dyn.EPlotControl.kControlsRowPercentage:
            case Dyn.EPlotControl.kControlsColPercentage:
                // turn off all but the given control
                this[Dyn.EPlotControl.kControlsCellPercentage] = (toggleControlID === Dyn.EPlotControl.kControlsCellPercentage);
                this[Dyn.EPlotControl.kControlsRowPercentage] = (toggleControlID === Dyn.EPlotControl.kControlsRowPercentage);
                this[Dyn.EPlotControl.kControlsColPercentage] = (toggleControlID === Dyn.EPlotControl.kControlsColPercentage);
                // and turn off control if was on
                this[toggleControlID] = !controlValue;
                break;
            case Dyn.EPlotControl.kControlsSectionPercentage:
            case Dyn.EPlotControl.kControlsSectionProportion:
                this[toggleControlID] = !controlValue;
                break;
        }
        this[Dyn.EPlotControl.kControlsMargins] = this.isControllingMarginsOrAxes();
    }

    /** Update this.plotShaper.bins.cels[].percent value */
    calcPercentByCell() {
        var plotCells = this.plotShaper.bins.cells,
            numCells = plotCells.length,
            numCases = 0,
            i, cell, caseCount; // Note: for multiple attributes there are 2+ icons per case

        // calc total number of cases included in plot (excluding filters, missing, etc)
        for( i = 0; i < numCells; i++ ) {
            numCases += plotCells[i].cases.length;
        }

        // calc percent in each cell
        for( i = 0; i < numCells; i++ ) {
            cell = plotCells[i];
            caseCount = cell.cases.length;
            cell.percent = (caseCount * 100 * Dyn.mathUtil.invertSafely( numCases ));
        }
    }

    /** Update this.plotShaper.bins.cels[].percent value, with Row-wise percentages */
    calcPercentByRow() {
        var plotCells = this.plotShaper.bins.cells,
            numCells = plotCells.length,
            numRowBins = this.plotShaper.bins.yBins.length,
            rowBinNumCases = Dyn.dataUtil.filledArray( numRowBins, 0 ),
            i, cell, caseCount; // Note: for multiple attributes there are 2+ icons per case

        // calc total number of cases in each row (excluding filters, missing, etc)
        for( i = 0; i < numCells; i++ ) {
            rowBinNumCases[plotCells[i].yBinIndex] += plotCells[i].cases.length;
        }

        // calc percent in each cell
        for( i = 0; i < numCells; i++ ) {
            cell = plotCells[i];
            caseCount = cell.cases.length;
            cell.percent = (caseCount * 100 / rowBinNumCases[cell.yBinIndex]);
        }
    }

    /** Update this.plotShaper.bins.cels[].percent value, with Colum-wise percentages */
    calcPercentByCol() {
        var plotCells = this.plotShaper.bins.cells,
            numCells = plotCells.length,
            numColumnBins = this.plotShaper.bins.xBins.length,
            colBinNumCases = Dyn.dataUtil.filledArray( numColumnBins, 0 ),
            i, cell, caseCount; // Note: for multiple attributes there are 2+ icons per case

        // calc total number of cases in each column (except cases excluded by filters + missing values)
        for( i = 0; i < numCells; i++ ) {
            colBinNumCases[plotCells[i].xBinIndex] += plotCells[i].cases.length;
        }

        // calc percent in each cell
        for( i = 0; i < numCells; i++ ) {
            cell = plotCells[i];
            caseCount = cell.cases.length;
            cell.percent = caseCount * 100 / colBinNumCases[cell.xBinIndex];
        }
    }

    /** adornments (specific to this element) */
    calcAdornments( percentageAdorns, iconPositions, xAxisView, yAxisView, noAnimation ) {
        var plotCells = this.plotShaper.bins.cells,
            plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
            percentName = "";

        // remove old adornments and clear the adornments list
        Dyn.plotUtil.removeAndEmptyArrayOfElements( percentageAdorns );
        measuresArray.length = 0; // remove old measures (values for summary table)

        // if Divider is on, Dyn.DividersElement will show divider-based counts/percents
        this.calcShowFlags();
        this.cellMarginY = this.cellMarginX = 0; // reset

        // calculate percentages
        if( this.showBinPercents ) {
            if( this.showRowPercents ) {
                percentName = this.calcPercentByRow();
            } else if( this.showColPercents ) {
                percentName = this.calcPercentByCol();
            } else {
                percentName = this.calcPercentByCell();
            }
        }

        // add count and percent text to plot, by adding text adornments to adornment list
        //	NOTE: if any percent is on, this element handles display of ALL counts and percents
        if( (this.showBinCounts || this.showBinPercents) && !this.suppressBinCountsPercents ) {
            this.addCellCountOrPercent( percentageAdorns, plotCells, this.showBinCounts, this.showBinPercents, noAnimation, percentName );
        }
        if( this.showPieCounts || this.showPiePercents ) { // show pie counts and/or percents
            this.addPieSectionCountOrPercent( percentageAdorns, plotCells, this.showPieCounts, this.showPiePercents, noAnimation );
        }
        if( this.showBarCounts || this.showBarPercents ) { // show bar counts and/or percents (for stacked or stretched bar chart only)
            this.addBarSectionCountOrPercent( percentageAdorns, plotCells, this.showBarCounts, this.showBarPercents, noAnimation );
        }
        if( this.showHistogramCounts || this.showHistogramPercents || this.showHistogramProportions ) {
            // show bar counts (for stacked or stretched bar chart only)
            this.addHistogramCountPercentProportion( percentageAdorns, plotCells, /*this.showHistogramCounts, this.showHistogramPercents,*/ noAnimation );
        }
        if( this.showBarSums ) { // stacked bars show bar counts w/o percents
            this.addBarTotals( percentageAdorns, plotCells, noAnimation );
        }
    }

    /**
     * Update our "which counts do we display" flags based on the plot configuration.
     * Designed to work no matter what the order that calcAdornments is called, so we don't
     * have to worry so much about order of evaluation.
     */
    calcShowFlags() {
        var countElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsCount ),
            dividerElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsDividers ),
            pieElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsPieChart ),
            barElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsBarChart ),
            histoElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsHistogram ),
            isDividerDisplayed = dividerElement ? dividerElement.isDisplayingDividers() : false;

        // get the count flags from the count element
        if( countElement )
            countElement.calcShowFlags(); // update flags to avoid any calculation order dependencies
        this.showBinCounts = countElement && countElement.showBinCounts;
        this.showPieCounts = countElement && countElement.showPieCounts;
        this.showBarCounts = countElement && countElement.showBarCounts;
        this.showBarSums = countElement && countElement.showBarSums;
        this.showHistogramCounts = countElement && countElement.showHistogramCounts;

        // update our flags
        this.showBinPercents = Boolean(
            this[Dyn.EPlotControl.kControlsCellPercentage] ||
            this[Dyn.EPlotControl.kControlsRowPercentage] ||
            this[Dyn.EPlotControl.kControlsColPercentage] );
        this.showPiePercents = Boolean( pieElement && this[Dyn.EPlotControl.kControlsSectionPercentage] );
        this.showBarPercents = Boolean( this[Dyn.EPlotControl.kControlsSectionPercentage] && barElement && barElement.allowBarSectionCounts( '%' ) )
        this.showHistogramPercents = Boolean( histoElement && this[Dyn.EPlotControl.kControlsSectionPercentage] );
        this.showHistogramProportions = Boolean( histoElement && this[Dyn.EPlotControl.kControlsSectionProportion] );
        this.showRowPercents =
            this[Dyn.EPlotControl.kControlsRowPercentage] &&
            (this.plotShaper.bins.xBins.length >= 2); // don't use if no rows
        this.showColPercents =
            this[Dyn.EPlotControl.kControlsColPercentage] &&
            (this.plotShaper.bins.yBins.length >= 2); // don't use if no rows

        // if Divider is on, so the calculations will be based on the dividers location and not the cell
        // if Count is on, this element will also display the counts
        this.suppressBinCountsPercents = isDividerDisplayed;
    }

    /**
     * Return true if this element has all toggle elements turned off, and thus can be removed from the plot.
     * @returns {boolean}
     */
    isElementWithAllTogglesOff() {
        return !(this[Dyn.EPlotControl.kControlsCellPercentage]
            || this[Dyn.EPlotControl.kControlsRowPercentage]
            || this[Dyn.EPlotControl.kControlsColPercentage]
            || this[Dyn.EPlotControl.kControlsSectionPercentage]
            || this[Dyn.EPlotControl.kControlsSectionProportion]); // true if no percents or proportions activated
    }

    /** How much space should axis reserve for text of percents? (reducing space for numeric axis, etc)
     * IMPORTANT: if there is a count and a percent, this function adds the space;
     * if percent with Dividers, don't add space here, let Dyn.Dividers add space and draw the count/percent
     * in each division.
     * @returns {{x: (number), y: (number)}}
     */
    getCellMargin() {
        this.calcShowFlags(); // update flags

        let representativeCell = new Dyn.mathUtil.Rect( 0, 0, 100, 100 ), // big enough to get the largest size from getCellFontSize()
            representativeFontSize = this.getCellFontSize( representativeCell, "Percentage CellMargin" );

        if( (this.showBinCounts || this.showBinPercents) && !this.suppressBinCountsPercents ) {
            //let representativeCell = new Dyn.mathUtil.Rect( 0,0,100,100 ); // big enough to get the largest size from getCellFontSize()
            this.cellMarginY = this.cellMarginX = this.kCellMarginRoomy + representativeFontSize;
            //console.log( "PercentageElement.getCellMargin():"+this.cellMarginY);
        } else {
            this.cellMarginY = this.cellMarginX = 0;
        }
        if( this.showHistogramPercents || this.showHistogramProportions ) {
            // add extra space for histogram percents or proportions (with or without histogram counts)
            let fontSizeToHeight = 2, // multiply for whitespace around text
                histogramTextHeight = fontSizeToHeight * representativeFontSize;
            this.cellMarginY += histogramTextHeight;
            this.cellMarginX += histogramTextHeight;
            //console.log( "PercentageElement.getCellMargin():"+this.cellMarginY);
        }
        return { x: this.cellMarginX, y: this.cellMarginY };
    }
} // class PercentageElement
Dyn.PercentageElement = PercentageElement;

Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsPercentage, Dyn.PercentageElement );
// make sure the old primary types also construct a Dyn.PercentageElement.
Dyn.PlotElemHelper.registerOldType( Dyn.EPlotControl.kControlsCellPercentage, (savedData) => Dyn.PercentageElement );
Dyn.PlotElemHelper.registerOldType( Dyn.EPlotControl.kControlsRowPercentage, (savedData) => Dyn.PercentageElement );
Dyn.PlotElemHelper.registerOldType( Dyn.EPlotControl.kControlsColPercentage, (savedData) => Dyn.PercentageElement );

/**
 * Dyn.DividersElement()
 */
class DividersElement extends CountPercentDividersElementBase {
    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityDividers,
            replaceType: Dyn.EPlotControl.kControlsDividers,
            primaryType: Dyn.EPlotControl.kControlsDividers,
            otherControls: [
                // Dyn.EPlotControl.kControlsAdornmentPositionOnly // NOTE: not possible with addition of this.getCellMargin()
            ] // for fast add/remove since icons/margins don't change
        }, Dyn );
        // Dividers locations are stored in (n,a) coordinates (Dyn.axisUtil.NACoord())
        //      location.n : location of divider line in normalized units where 0 is axis minimum, 1 is axis maximum [0-1]
        //      location.a : location of divider line in attribute units, e.g. meters for a "length" attribute
        // Dividers plot in .a units if there is a valid numeric attribute, otherwise in .n units if not (mostly when attribute has changed)
        this.dividersLocations = [];    // for [1+ Y-axis bins][0+ dividers], NACoord of divider line on X axis
        this.axisAttributeID = "";      // if non-empty, track dividers in the attribute's coordinates
        this.showBinCounts = false;     // if true, this elements shows counts of elements between dividers on X, within bins on Y axis if any
        this.showBinPercents = false;   // if true, this elements shows percents of elements between dividers on X, within bins on Y axis if any
        this.showDividers = true;
    }

    get kUndoAction() {
        return 'Dividers';
    }

    static #defaultFirstFraction = 0.25;
    static #defaultSecondFraction = 0.75;

    // upon save keep track of the dividers position
    save() {
        let plotElementModel = super.save(); // call inherited save()

        // if dividers on the screen, save array of divider locations, each an arrays of NACoords
        if( this.isDisplayingDividers() ) {
            plotElementModel.dividersLocations = this.dividersLocations.map(
                array => array.map( naCoord => naCoord.save() ) );
        }
        return plotElementModel;
    }

    // Restore the plot, set the divider position fraction
    restore( savedData ) {
        super.restore( savedData ); // restore base model

        // restore saved array of divider locations, each an arrays of NACoords
        if( savedData.dividersLocations ) {
            this.dividersLocations = savedData.dividersLocations.map(
                array => array.map( naCoord => Dyn.axisUtil.NACoord.restore( naCoord ) ) );
        }
        this.isNewlyRestoredData = true; // to prevent calcAxis() from stripping attribute coordinates
    }

    /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
    calcAxis( ioWhatChanged, subViewRef ) {
        // update the X-axis attribute ID, and clear any old attribute coordinates
        // This causes the divider lines to stay in the same relative view position (via normalized coordinates)
        // even when the axis has completely different attribute coordinates.
        let result = Dyn.axisUtil.isNewAttributeCoord( this.axisAttributeID, subViewRef.xAxisView, this.isNewlyRestoredData )
        if( result.needToStripCoords ) {
            this.dividersLocations.forEach( function( binArray ) {
                binArray.forEach( function( naCoord ) {
                    naCoord.stripAttributeCoord();
                } );
            } );
        }
        this.fixDividerLocations( subViewRef.xAxisView );
        this.axisAttributeID = result.newAttributeID;
        this.showDividers = subViewRef.xAxisView.isNumberLineAxis();
        delete this.isNewlyRestoredData; // no longer newly restored data (issue #555)
    }

    // Check if the dividers is currently been displayed
    isDisplayingDividers() {
        return this.showDividers;
    }

    // For the text element between dividers
    calcTextPosition( startX, endX ) {
        return (endX - startX) / 2 + startX;
    }

    /**
     * Calculate count and percentage of cases in the cell in the division (between two dividers).
     * Case values exactly matching a divider value will go into the *upper* division, which
     * is a common convention of statistical cutpoints.
     * @param leftX -- left X value of division, in view coordinates, or undefined if left value of left-most division
     * @param rightX -- right X value of division, in view coordinates, or undefined if right value of right-most division
     * @param xAxisView {Dyn.AxisView}
     * @param cell -- cell data from Dyn.PlotShaper, dividing the plot into cells because of categories on axis.
     * @param iconPositions -- case position data
     * @returns {{count: string, text: string, percent: string}}
     */
    calcCountPercentageValues( leftX, rightX, xAxisView, cell, iconPositions ) {

        function percentString( casesInBlock, caseCount, precision ) {
            let percent = casesInBlock * 100 / caseCount;
            return Dyn.mathUtil.roundIfInteger( Dyn.mathUtil.roundToDecimalPlaces( percent, precision ) ).toString();
        }

        let coordMap = xAxisView.coordMap,
            attribute = xAxisView.getAttribute(),
            attributeCaseArray = attribute.cases,
            caseCount = cell.cases.length;

        // get the range of the division, in attribute coordinates
        let isMaxDivision = !Number.isFinite( attribute.isSortAscending ? rightX : leftX ),
            isMinDivision = !Number.isFinite( attribute.isSortAscending ? leftX : rightX );
        let dataRange = new Dyn.mathUtil.Range( coordMap.inverseTransform( leftX ), coordMap.inverseTransform( rightX ) );
        if( !attribute.isSortAscending ) {
            // left is max, right is min, swap them (even if one or the other is NaN
            [ dataRange.min, dataRange.max ] = [ dataRange.max, dataRange.min ];
        }
        let displayRange = new Dyn.mathUtil.Range( dataRange.min, dataRange.max );
        const viewDelta = 0.0001; // slight offset in view coordinates to handle rounding errors
        dataRange.min -= coordMap.inverseScaleAbs( viewDelta );
        dataRange.max -= coordMap.inverseScaleAbs( viewDelta );

        // handle undefined min or max value by swapping in axis or data min or max
        let attrRange = new Dyn.mathUtil.Range( attribute.stats.min, attribute.stats.max ),
            axisRange = xAxisView.getNumericAxisRange();
        if( isMinDivision ) {
            // min of lower range uses lowest axis or data value, or first divider line if past axis or data (offscreen)
            dataRange.min = Math.min( attrRange.min, axisRange.min, dataRange.max );
            displayRange.min = Math.min( attrRange.min, axisRange.min, displayRange.max );
        }
        if( isMaxDivision ) {
            // min of upper range uses highest of axis or data value, or last divider line if past axis or data (offscreen)
            dataRange.max = Math.max( attrRange.max, axisRange.max, dataRange.min ) + coordMap.inverseScaleAbs( viewDelta );
            displayRange.max = Math.max( attrRange.max, axisRange.max, displayRange.min );
        }

        // how many cases in this cell are in the range? [min-max)
        // Note: cell.cases[] has case index (c) into attribute.cases
        let casesInBlock = 0;
        cell.cases.forEach( ( positionIndex ) => {
            Dyn.assertArrayIndex( iconPositions, positionIndex, "invalid position index" );
            let p = iconPositions[positionIndex],
                v = attributeCaseArray[p.caseIndex];
            if( p.xAttrIndex === 0 &&
                v >= dataRange.min &&
                v < dataRange.max ) {
                Dyn.assertArrayIndex( attributeCaseArray, p.caseIndex, "invalid case index" );
                casesInBlock++;
            }
        } );

        // convert to strings with the correct number of decimal places/precision
        let values = { count: "", percent: "", text: "", n: casesInBlock, total: caseCount, interval: "" },
            precision = 1 + Dyn.preferences.getPrecision(); // 1 + user-override decimal places
        if( this.showBinPercents && this.showBinCounts ) {
            // in the bin cases are 0, avoid divide by zero
            if( caseCount > 0 ) {
                values.count = casesInBlock.toString();
                values.percent = percentString( casesInBlock, caseCount, precision ) + "%";
                values.text = values.count + " (" + values.percent + ")";
            } else {
                values.count = "0";
                values.percent = "0%";
                values.text = values.count + " (" + values.percent + ")";
            }
        } else if( this.showBinPercents ) {
            if( caseCount > 0 ) {
                values.percent = percentString( casesInBlock, caseCount, precision ) + "%";
                values.text = values.percent;
            } else {
                values.percent = "0%";
                values.text = values.percent;
            }
        } else if( this.showBinCounts ) {
            values.count = casesInBlock.toString();
            values.text = values.count;
        }
        values.interval = this.dividerIntervalToString( displayRange.min, displayRange.max, xAxisView, isMinDivision, isMaxDivision );

        return values;
    }

    /** Get the display string for the interval of one division (2 dividers make 3 divisions). */
    dividerIntervalToString( min, max, axisView, isMinDivision, isMaxDivision ) {
        let intervalString;
        {   // new style intervals "1.23 to 34.5"
            function roundUpOrDown( value, isUp, axisView ) {
                let onePixelScale = Math.abs( Dyn.mathUtil.invertSafely( axisView.coordMap.scale ) ),
                    rounding = { precision: Dyn.preferences.getPrecision(), min: 0 },// add user precision, if any
                    roundMax = Dyn.mathUtil.roundToScale( value, onePixelScale, rounding ),
                    intervalGap = Math.pow( 10, -rounding.precision );
                return roundMax + (isUp ? intervalGap : -intervalGap);
            }

            if( !isMaxDivision ) { // adjust max of lower range,e.g. "10 to 14.9","15-20" instead of "10 to 15","15 to 20"
                max = roundUpOrDown( max, false /* down */, axisView );
            } else if( min === max ) {  // adjust max of upper range if 0 width, e.g. "20.01 to 20.02" instead of "20.01 to 20.01"
                max = roundUpOrDown( max, true /* up */, axisView );
            }
            let minMaxString = {
                min: this.dividerLocationToString( min, axisView ),
                max: this.dividerLocationToString( max, axisView )
            };
            intervalString = Dyn.loc( "{{min}} to {{max}}", minMaxString );
        }
        // {
        //     // old style intervals "1.23 <= x < 3.45"
        //     let minMaxString = {
        //         min: this.dividerLocationToString(min, axisView),
        //         max: this.dividerLocationToString(max, axisView)
        //     };
        //     if( isMinDivision ){
        //         intervalString = Dyn.loc("x < {{max}}", minMaxString);
        //     } else if( isMaxDivision ){
        //         intervalString = Dyn.loc("{{min}} <= x", minMaxString);
        //     } else {
        //         intervalString = Dyn.loc("{{min}} <= x < {{max}}", minMaxString);
        //     }
        // }
        return intervalString;
    }

    dividerLocationToString( attrValue, axisView ) {
        // round value to same precision as one pixel distance on axis
        let attribute = axisView.getAttribute(),
            onePixelScale = Math.abs( Dyn.mathUtil.invertSafely( axisView.coordMap.scale ) ),
            options = { precision: Dyn.preferences.getPrecision() }; // add user precision, if any
        attrValue = Dyn.mathUtil.roundToScale( attrValue, onePixelScale, options );
        return attribute.axisValueToString( attrValue, options.precision );
    }

    calcAdornments( dividersAdorns, iconPositions, xAxisView, yAxisView, noAnimation ) {
        var thisPlotShaper = this.plotShaper,
            dividersElem = this,	//access to the divider element
            plotCells = this.plotShaper.bins.cells,
            plotViewPaper = this.plotShaper.adornments.paper,
            plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
            numCells = plotCells.length,
            //hoverColor     = Dyn.plotUtil.dividerColor,
            dividerSets = [],
            leftBound, rightBound;	//It will always stay same as divider work with xAxis only.
        let viewOnly = Dyn.preferences.isViewOnly();

        function overScope( e ) {
            //e.currentTarget.style.cursor = Dyn.plotUtil.cursorHorizontalBothSide;
            this.toFront();
            Dyn.toolTip.show( this, e, { x: this.attrs.x, y: this.attrs.y } );
        }

        function outScope( e ) {
            //e.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
            if( Dyn.toolTip.isMyElement( this ) )
                Dyn.toolTip.hide();
            thisPlotShaper.layerAdornments(); // restore original layering
        }

        function start() {
            var lineElement = this;
            lineElement.data( "sx", lineElement.attr( "path" )[0][1] );
            //lineElement.attr( "cursor", Dyn.plotUtil.cursorHorizontalBothSide );
            // for undo/redo
            dividersElem.preMoveSavedData = dividersElem.save();
        }

        function move( dx, dy, x, y, e ) {
            // move the line, and update Dividers.dividersLocations
            var coverElement = this,
                cellValue = coverElement.data( "cell" ),
                binId = coverElement.data( "dividerSetId" ),
                rank = coverElement.data( "dividerRank" ),
                xPosition = coverElement.data( "sx" ) + dx,
                pathArray = coverElement.attr( "path" ),
                setElements = dividerSets[coverElement.data( "dividerSetId" )],
                lineElement = setElements[(rank == 1) ? 0 : 2],
                otherDividerPathArray = setElements[(rank == 1) ? 2 : 0].attr( "path" ),
                isFirstDivider = (rank === 1),
                xPositionOffset = 5;

            // Make sure dividers are between left and right bound
            if( xPosition <= leftBound || xPosition >= rightBound )
                return;

            // restrict dividers to crossover each other
            if( isFirstDivider ) {
                if( xPosition + xPositionOffset >= otherDividerPathArray[0][1] )
                    return;
            } else {
                if( xPosition - xPositionOffset <= otherDividerPathArray[0][1] )
                    return;
            }

            // snap coordinate to nearby tickmarks, then save the line position
            let lineCoords = dividersElem.dividersLocations[binId][rank - 1],
                snapToTickmarks = true;
            lineCoords.updateFromViewCoord( xPosition, xAxisView, snapToTickmarks, Dyn.plotUtil.refLineSnapDistance );
            xPosition = lineCoords.getViewCoord( xAxisView );

            // change the value of tooltip based on the location of the element - recalculate
            coverElement.data( "tooltip", dividersElem.dividerLocationToString( xAxisView.coordMap.inverseTransform( xPosition ), xAxisView ) );
            if( Dyn.toolTip.isMyElement( coverElement ) )
                Dyn.toolTip.update( coverElement, e, { x: coverElement.attrs.x, y: coverElement.attrs.y } ); // update value shown by overScope()
            else
                Dyn.toolTip.show( coverElement, e, { x: coverElement.attrs.x, y: coverElement.attrs.y } );

            // update the line
            pathArray[0][1] = xPosition;
            pathArray[1][1] = xPosition;
            lineElement.attr( "path", pathArray );
            coverElement.attr( "path", pathArray );

            // If first, change the first and centerText
            if( isFirstDivider ) {
                let leftTextElement = setElements[4],
                    centerTextElement = setElements[5],
                    secondDividerLoc = otherDividerPathArray[0][1]

                let values = dividersElem.calcCountPercentageValues( undefined, xPosition, xAxisView, cellValue, iconPositions );
                leftTextElement.attr( { "x": dividersElem.calcTextPosition( leftBound, xPosition ) } );
                leftTextElement.attr( { "text": values.text } );

                values = dividersElem.calcCountPercentageValues( xPosition, secondDividerLoc, xAxisView, cellValue, iconPositions );
                centerTextElement.attr( { "x": dividersElem.calcTextPosition( xPosition, secondDividerLoc ) } );
                centerTextElement.attr( { "text": values.text } );
                // else update the center and lastText
            } else {
                let centerTextElement = setElements[5],
                    rightTextElement = setElements[6],
                    firstDividerLoc = otherDividerPathArray[0][1];

                let values = dividersElem.calcCountPercentageValues( firstDividerLoc, xPosition, xAxisView, cellValue, iconPositions );
                centerTextElement.attr( { "x": dividersElem.calcTextPosition( firstDividerLoc, xPosition ) } );
                centerTextElement.attr( { "text": values.text } );

                values = dividersElem.calcCountPercentageValues( xPosition, undefined, xAxisView, cellValue, iconPositions );
                rightTextElement.attr( { "x": dividersElem.calcTextPosition( xPosition, rightBound ) } );
                rightTextElement.attr( { "text": values.text } );
            }
        }

        function up() {
            var postMoveSavedData = dividersElem.save();
            mediator.publish( "plot:dragDivider", {
                saveData: dividersElem.preMoveSavedData,
                restoreData: postMoveSavedData
            } );
            delete dividersElem.preMoveSavedData; // forget about saved divider data, undo manager has it now
            setTimeout( () => {
                // Defer setAsKeyboardFocus() for 100ms, to fix problem where repeated keypresses lose focus about 1 in 5 times.
                Dyn.plotUtil.setAsKeyboardFocus( this ); // keep keyboard focus for arrow or delete key after drag
            }, 100 );
        }

        function keyDownHandler( e ) {
            switch( e.key ) { // const {key} = event; ES6+
                case "Delete":
                case "Backspace":
                    // remove the dividers from the plot
                    mediator.publish( "plot:removePlotElement", { controlID: dividersElem.primaryType } );
                    break;
                case "ArrowLeft":
                case "ArrowRight":
                case "ArrowUp":
                case "ArrowDown": {   // move this element one pixel in arrow's direction
                    let dist = Dyn.plotUtil.arrowKeyAcceleratedDistance( e.key ),
                        dragLine = this;
                    start.call( dragLine, e );
                    move.call( dragLine, dist.dx, dist.dy, undefined, undefined, e );
                    up.call( dragLine, e );
                }
                    break;
            }
        }

        function isInView( viewCoord ) {
            return (viewCoord >= leftBound) && (viewCoord <= rightBound);
        }

        function createDividerElement( pathString, optionalAriaLabel ) {
            // create a visible line and a wider invisible cover for easy selection
            let line = plotViewPaper.path( pathString ).hover( overScope, outScope ),
                cover = plotViewPaper.path( pathString ).hover( overScope, outScope );
            if( !viewOnly ) {
                cover.drag( move, start, up )
                    .attr( "cursor", Dyn.plotUtil.cursorHorizontalBothSide );
                Dyn.plotUtil.setKeyboardFocusable( cover, keyDownHandler.bind( cover ), optionalAriaLabel );
            }
            return [ line, cover ];
        }

        function getDividerElement( dividerSet, setIndex1, setIndex2 ) {
            let line = dividerSet[setIndex1],
                cover = dividerSet[setIndex2];
            return [ line, cover ];
        }

        function updateDividerElement( line, cover, setID, rank, cell, tooltip ) {
            Dyn.assert( line && line.type === "path" );
            line.attr( { stroke: Dyn.plotUtil.dividerColor, "stroke-width": Dyn.plotUtil.dividerLineWidth } );

            Dyn.assert( cover && cover.type === "path" );
            let coverWidth = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.dividerLineCoverWidth );
            cover.attr( {
                stroke: "white", opacity: Dyn.plotUtil.opacityInvisibleButDraggable,
                "stroke-width": coverWidth
            } );
            cover.data( "dividerSetId", setID );
            cover.data( "dividerRank", rank );
            cover.data( "cell", cell );
            cover.data( "tooltip", tooltip );
        }

        // update flags
        this.calcShowFlags( xAxisView );

        // create or update the dividers and measures (take care to update existing elements so drag + keyboard handling work correctly)
        let newNumAdornments = xAxisView.isNumberLineAxis() ? numCells : 0,     // one divider set per cell, or none if no numberline
            createAdornments = (dividersAdorns.length !== newNumAdornments);  // else just update adornments if the number has not changed

        if( createAdornments ) {
            Dyn.plotUtil.removeAndEmptyArrayOfElements( dividersAdorns );
        }
        measuresArray.length = 0; // remove old measures (values for summary table)

        // If it already exists on the plot, don't reset it
        // And use the same position for the display of the new plots
        if( xAxisView.isNumberLineAxis() ) {
            // iterate over the plotCells and add the case count element to the plot
            let attributeID = xAxisView.getAttribute().id; // count is same if 0, 1, or 2+ attributes on axis (unless attribute creates bins)
            this.fixDividerLocations( xAxisView );

            for( let i = 0; i < numCells; i++ ) {
                let cell = plotCells[i];
                leftBound = cell.left();
                rightBound = cell.right();
                let setMemberIndex = 0, testCount = 0, totalCount = cell.cases.length;

                // Push the default position only if the previous default position is not available
                // otherwise use the same position to avoid reseting of the divider
                if( typeof this.dividersLocations[i] === "undefined" ) {
                    this.dividersLocations.push( [
                        new Dyn.axisUtil.NACoord( DividersElement.#defaultFirstFraction ),
                        new Dyn.axisUtil.NACoord( DividersElement.#defaultSecondFraction ) ] );
                }
                if( createAdornments ) {
                    dividerSets.push( plotViewPaper.set() );
                    dividersAdorns.push( dividerSets[i] );  // add the dividerSet to the dividersAdorns
                } else {
                    dividerSets.push( dividersAdorns[i] );
                }
                Dyn.assert( dividerSets[i] && dividerSets[i].type === "set" );
                Dyn.assert( dividersAdorns[i] && dividersAdorns[i].type === "set" );

                // first divider
                let firstDividerLoc = this.dividersLocations[i][0].getViewCoord( xAxisView ),
                    sx = firstDividerLoc,
                    sy = cell.y,
                    ey = cell.y + cell.height,
                    lineAndCover;
                let pathString = "M" + sx + "," + sy + "L" + sx + "," + ey,
                    tooltip = this.dividerLocationToString( this.dividersLocations[i][0].a, xAxisView );
                if( createAdornments ) {
                    lineAndCover = createDividerElement( pathString, Dyn.loc( "Divider Line 1" ) );
                    dividerSets[i].push( ...lineAndCover );
                } else {
                    lineAndCover = getDividerElement( dividerSets[i], setMemberIndex++, setMemberIndex++ );
                    lineAndCover[0].attr( { path: pathString } );
                    lineAndCover[1].attr( { path: pathString } );
                }
                updateDividerElement( lineAndCover[0], lineAndCover[1], i, 1, cell, tooltip );

                // second divider
                let secondDividerLoc = sx = this.dividersLocations[i][1].getViewCoord( xAxisView );
                pathString = "M" + sx + "," + sy + "L" + sx + "," + ey;
                tooltip = this.dividerLocationToString( this.dividersLocations[i][1].a, xAxisView );
                if( createAdornments ) {
                    lineAndCover = createDividerElement( pathString, Dyn.loc( "Divider Line 2" ) );
                    dividerSets[i].push( ...lineAndCover );
                } else {
                    lineAndCover = getDividerElement( dividerSets[i], setMemberIndex++, setMemberIndex++ );
                    lineAndCover[0].attr( { path: pathString } );
                    lineAndCover[1].attr( { path: pathString } );
                }
                updateDividerElement( lineAndCover[0], lineAndCover[1], i, 2, cell, tooltip );

                // first text block / lowest data range
                let leftTextElement,
                    textX = this.calcTextPosition( leftBound, firstDividerLoc ),
                    textY = cell.y,
                    cellCalc = this.calcCountPercentageValues( undefined, firstDividerLoc, xAxisView, cell, iconPositions );
                testCount += cellCalc.n;
                if( createAdornments ) {
                    leftTextElement = plotViewPaper.text( textX, textY, cellCalc.text );
                    dividerSets[i].push( leftTextElement );
                } else {
                    leftTextElement = dividerSets[i][setMemberIndex++];
                    leftTextElement.attr( { x: textX, y: textY, text: cellCalc.text } );
                }
                Dyn.assert( leftTextElement && leftTextElement.type === "text" );
                leftTextElement.attr( { "font-size": this.getCellFontSize( cell, "Divider 1" ) } );
                leftTextElement.attr( { "transform": Dyn.plotUtil.getTextTransform( leftTextElement, "center,top" ) } );
                Dyn.plotUtil.showOrHideElement( leftTextElement, isInView( firstDividerLoc ) );
                this.addMeasure( plotMeasures, measuresArray, cellCalc, 0, cell, attributeID );

                // second block / middle data range
                let centerTextElement;
                textX = this.calcTextPosition( firstDividerLoc, secondDividerLoc );
                cellCalc = this.calcCountPercentageValues( firstDividerLoc, secondDividerLoc, xAxisView, cell, iconPositions );
                testCount += cellCalc.n;
                if( createAdornments ) {
                    centerTextElement = plotViewPaper.text( textX, textY, cellCalc.text );
                    dividerSets[i].push( centerTextElement );
                } else {
                    centerTextElement = dividerSets[i][setMemberIndex++];
                    centerTextElement.attr( { x: textX, y: textY, text: cellCalc.text } );
                }
                Dyn.assert( centerTextElement && centerTextElement.type === "text" );
                centerTextElement.attr( { "font-size": this.getCellFontSize( cell, "Divider 2" ) } );
                centerTextElement.attr( { "transform": Dyn.plotUtil.getTextTransform( centerTextElement, "center,top" ) } );
                Dyn.plotUtil.showOrHideElement( centerTextElement, isInView( firstDividerLoc ) || isInView( secondDividerLoc ) );
                this.addMeasure( plotMeasures, measuresArray, cellCalc, 1, cell, attributeID );

                // third block / upper data range
                let rightTextElement;
                textX = this.calcTextPosition( secondDividerLoc, rightBound );
                cellCalc = this.calcCountPercentageValues( secondDividerLoc, undefined, xAxisView, cell, iconPositions );
                testCount += cellCalc.n;
                if( createAdornments ) {
                    rightTextElement = plotViewPaper.text( textX, textY, cellCalc.text );
                    dividerSets[i].push( rightTextElement );
                } else {
                    rightTextElement = dividerSets[i][setMemberIndex++];
                    rightTextElement.attr( { x: textX, y: textY, text: cellCalc.text } );
                }
                Dyn.assert( rightTextElement && rightTextElement.type === "text" );
                rightTextElement.attr( { "font-size": this.getCellFontSize( cell, "Divider 3" ) } );
                rightTextElement.attr( { "transform": Dyn.plotUtil.getTextTransform( rightTextElement, "center,top" ) } );
                Dyn.plotUtil.showOrHideElement( rightTextElement, isInView( secondDividerLoc ) );
                this.addMeasure( plotMeasures, measuresArray, cellCalc, 2, cell, attributeID );

                if( testCount !== totalCount ) {
                    console.log( "count of cases in divisions (" + testCount + ") does not match count of cases in cell (" + totalCount + ")." );
                }
                // Dyn.assert( testCount===totalCount, "count of cases in divisions ("+testCount+") does not match count of cases in cell ("+totalCount+").");
            }
        }
        Dyn.assert( dividersAdorns.length === newNumAdornments, "number of divider adornments calculation incorrect" );
    }

    /** add a count, percent, and interval measure for the Summary View, for one row of the Divisions table */
    addMeasure( plotMeasures, measuresArray, values, divBinIndex, cell, attributeID ) {
        if( values.count ) { // if count string is present
            plotMeasures.addToMeasuresArray( measuresArray, {
                setID: 'divider',
                measureName: Dyn.loc( "Count" ),
                value: values.count,
                attributeID: attributeID,
                divBinIndex: divBinIndex
            }, cell.xBinIndex, cell.yBinIndex );
        }
        if( values.percent ) { // if percent string is present
            plotMeasures.addToMeasuresArray( measuresArray, {
                setID: 'divider',
                measureName: Dyn.loc( "Percent" ),
                value: values.percent,
                attributeID: attributeID,
                divBinIndex: divBinIndex
            }, cell.xBinIndex, cell.yBinIndex );
        }
        if( values.interval ) { // if interval string is present
            plotMeasures.addToMeasuresArray( measuresArray, {
                setID: 'divider',
                measureName: Dyn.loc( "Interval" ),
                value: values.interval,
                attributeID: attributeID,
                divBinIndex: divBinIndex
            }, cell.xBinIndex, cell.yBinIndex );
        }
    }

    /** determine if this plot element should display counts and/or percents */
    calcShowFlags( xAxisView ) {
        var countElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsCount ),
            percentageElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsPercentage );

        // update our flags
        this.showDividers = xAxisView.isNumberLineAxis(); // note: also updated by calcAxis()

        // get the count flags from the count element
        if( countElement ) {
            countElement.calcShowFlags(); // update flags
            Dyn.assert( this.showDividers ? countElement.suppressBinCountsPercents : true, "Count element should not show counts when dividers are on" );
        }
        this.showBinCounts = this.showDividers && countElement && countElement.showBinCounts;

        if( percentageElement ) {
            percentageElement.calcShowFlags();
            Dyn.assert( this.showDividers ? percentageElement.suppressBinCountsPercents : true, "Percentage element should not show percents when dividers are on" );
        }
        this.showBinPercents = this.showDividers && percentageElement && percentageElement.showBinPercents;
    }

    /**
     * Get the human-readable names of the divisions
     * Note that if there are N dividers on X, and M category bins on Y, there will be ((N+1) x M) bin names.
     * but for now we just show the N + 1 names as "divider 1", "divider 2", "divider3".
     */
    getDivisionNames() {

        if( false ) {
            // possible future enhancement: if 1 attribute and 1 cell use "12.3 to 34.5" as division name
        } else {
            // if 0 or 2+ attributes, use "Division [1/2/3]" as name, and add range as cell data
            function getDivisionName( index ) {
                return Dyn.loc( "{{division}} {{index}}", { division: "division", index: "" + (index + 1) } );
            }

            let whichBin = 0,
                whichArray = this.dividersLocations[whichBin],
                namesArray = [];
            if( whichArray && whichArray.length > 0 ) {
                whichArray.forEach( function( value, index ) {
                    namesArray.push( getDivisionName( index ) );
                } );
                // one more; N+1 dividers for N divisions
                namesArray.push( getDivisionName( whichArray.length ) );
            }
            return namesArray;
        }
    }

    getCellMargin() {
        // if( this.showBinCounts || this.showBinPercents ) {
        let representativeCell = new Dyn.mathUtil.Rect( 0, 0, 100, 100 ); // big enough to get the largest size from getCellFontSize()
        this.cellMarginY = this.cellMarginX = this.kCellMarginRoomy + this.getCellFontSize( representativeCell, "Divider Cell Margin" );
        return { x: this.cellMarginX, y: this.cellMarginY };
    }

    /**
     * check to see if the attribute sort order has changed from ascending to descending in the axis,
     * if so, adjust the current divider locations to match, because they are expected to be ordered
     * left to right in view coordinates.
     */
    fixDividerLocations( xAxisView ) {
        let div0 = this.dividersLocations[0],
            hasAttributeCoordinates = (div0 && Number.isFinite( div0[0].a )),
            hasAxisCoordinates = (xAxisView.coordMap !== null);
        if( hasAttributeCoordinates && hasAxisCoordinates ) {
            let divLeft = div0[0],
                divRight = div0[1];
            Dyn.assert( divLeft.n < divRight.n );
            let isAscendingInLastLocation = (divLeft.a < divRight.a),
                a0 = xAxisView.coordMap.fractionToSource( divLeft.n ),
                a1 = xAxisView.coordMap.fractionToSource( divRight.n ),
                isAscendingNow = (a0 < a1);
            if( isAscendingInLastLocation !== isAscendingNow ) {
                // strip attribute coordinates; use normalized coordinates instead
                // this prevents the left divider from moving to the right of the right divider, which we don't allow.
                this.dividersLocations.forEach( binArray =>
                    binArray.forEach( div =>
                        div.stripAttributeCoord() ) );
                //console.log("fixDividerLocations fix made");
            }
        }
    }

} // class DividersElement
Dyn.DividersElement = DividersElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsDividers, Dyn.DividersElement );

}; // wrapDynCountElement
